import { useCallback, useState } from "react";

import { SubscriptionProvider } from "@prisma/client";

import {
  PRICE_ID_TO_INTERVAL_MAP,
  PRICE_ID_TO_PLAN_MAP,
  ProductId,
  type TPriceId,
} from "@musicfy/contants/Subscriptions";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import {
  api,
  getSubscriptionChangeBlockReason,
  getSubscriptionChangeMessage,
  SubscriptionChangeBlockReason,
  parsePaddleAmount,
  resolvePaddleCurrencyCode,
} from "@musicfy/utils";
import { StripePlanChangeKind } from "@musicfy/utils/stripePlanChange";

import { usePaddleContext } from "..";
import { useSubscriptionContext } from "../../SubscriptionProvider";
import { PaddleScreens } from "../types";

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

export const usePaddleUpgrade = () => {
  const { upgradePreviewContext, setActivePaddleScreen, origin, setOrigin } =
    usePaddleContext();
  const { subscription, setSubscription, paddleSubscription, isPaddleLoading } =
    useSubscriptionContext();
  const { sendToastMessage } = useToastContext();

  const { trackEvent } = useAnalytics();

  const [isProcessing, setIsProcessing] = useState(false);

  const {
    upgradePreviewData,
    setUpgradePreviewData,
    setIsUpgradeUpdating,
    isUpgradeUpdating,
    stripeChangePreview,
    setStripeChangePreview,
  } = upgradePreviewContext;

  const returnToPricing = useCallback(() => {
    setUpgradePreviewData(undefined);
    setStripeChangePreview(undefined);
    setActivePaddleScreen(PaddleScreens.PRICING);
  }, [setActivePaddleScreen, setStripeChangePreview, setUpgradePreviewData]);

  const getStripeChangePreview =
    api.subscription.getStripeChangePreview.useMutation({
      onSuccess: setStripeChangePreview,
      onError: (error) => {
        sendToastMessage(
          getErrorMessage(error, "Unable to preview this plan change."),
          { severity: "error" }
        );
        returnToPricing();
      },
    });

  const changeStripeSubscription =
    api.subscription.changeStripeSubscription.useMutation({
      onSuccess: (data) => {
        setSubscription(data);
        sendToastMessage(
          stripeChangePreview?.kind === StripePlanChangeKind.downgrade
            ? "Your downgrade is scheduled for the next billing date."
            : "Your plan has been upgraded.",
          { severity: "success" }
        );
        setStripeChangePreview(undefined);
        setActivePaddleScreen(null);
      },
      onError: (error) => {
        sendToastMessage(
          getErrorMessage(error, "Unable to change your Stripe plan."),
          { severity: "error" }
        );
      },
    });

  const getUpgradePreview = api.subscription.getUpgradePreview.useMutation({
    onSuccess: (data) => {
      if (!data) {
        sendToastMessage(
          getSubscriptionChangeMessage(
            SubscriptionChangeBlockReason.paddleUnavailable
          ),
          { severity: "error" }
        );
        returnToPricing();
        return;
      }

      setUpgradePreviewData(data);
    },
    onError: (error) => {
      sendToastMessage(
        getErrorMessage(
          error,
          getSubscriptionChangeMessage(
            SubscriptionChangeBlockReason.paddleUnavailable
          )
        ),
        { severity: "error" }
      );
      returnToPricing();
    },
  });
  const changeSubscription = api.subscription.changeSubscription.useMutation({
    onMutate: () => {
      setIsProcessing(true);
    },
    onSettled: () => {
      setIsProcessing(false);
    },
    onSuccess: (data) => {
      if (!data) {
        sendToastMessage(
          "Your plan may have been updated. Please reload the page to confirm.",
          { severity: "warning" }
        );
        setActivePaddleScreen(null);
        return;
      }
      const isScheduledDowngrade = !!data.pendingPlan;
      const change = isScheduledDowngrade ? "Downgrade" : "Upgrade";
      const immediateTotal =
        upgradePreviewData?.immediate_transaction?.details?.totals?.total;
      const revenueCurrency = resolvePaddleCurrencyCode(
        !!upgradePreviewData,
        upgradePreviewData?.currency_code
      );
      const bookedRevenue = immediateTotal
        ? parsePaddleAmount(immediateTotal, revenueCurrency)
        : 0;
      trackEvent("Subscription Change", {
        previousPlan: subscription?.plan,
        previousInterval: subscription?.interval,
        newPlan: data.pendingPlan ?? data.plan,
        newInterval: data.pendingInterval ?? data.interval,
        change: change,
        origin: origin,
        price: bookedRevenue,
        $revenue: bookedRevenue,
        currency: revenueCurrency,
      });
      setSubscription(data);
      sendToastMessage(
        isScheduledDowngrade
          ? "Your downgrade is scheduled for the next billing date."
          : "Your plan has been upgraded.",
        {
          severity: "success",
        }
      );
      setActivePaddleScreen(null);
    },
    onError: (error) => {
      sendToastMessage(
        getErrorMessage(
          error,
          getSubscriptionChangeMessage(
            SubscriptionChangeBlockReason.paddleUnavailable
          )
        ),
        { severity: "error" }
      );
      returnToPricing();
    },
  });

  const startUpgrade = useCallback(
    (priceId: TPriceId) => {
      if (!subscription) {
        return;
      }

      const paddleLoadFailed =
        subscription.provider === SubscriptionProvider.paddle &&
        !subscription.subscriptionId.includes("temp") &&
        !isPaddleLoading &&
        !paddleSubscription;

      const blockReason = getSubscriptionChangeBlockReason(subscription, {
        paddleLoadFailed,
        requireNotCanceling: true,
      });

      if (blockReason) {
        sendToastMessage(getSubscriptionChangeMessage(blockReason), {
          severity: "error",
        });

        // Stay on pricing (or close if we never opened upgrade) with a message.
        if (
          blockReason === SubscriptionChangeBlockReason.paymentMethodRequired
        ) {
          setActivePaddleScreen(null);
          return;
        }

        returnToPricing();
        return;
      }

      if (subscription.provider === SubscriptionProvider.stripe) {
        setStripeChangePreview(undefined);
        setActivePaddleScreen(PaddleScreens.STRIPE_UPGRADE);
        getStripeChangePreview.mutate({
          subscriptionId: subscription.subscriptionId,
          priceId,
        });

        const product = PRICE_ID_TO_PLAN_MAP[priceId];
        const interval = PRICE_ID_TO_INTERVAL_MAP[priceId];
        trackEvent("Upgrade Started", {
          plan: product,
          interval,
          origin,
        });
        return;
      }

      setActivePaddleScreen(PaddleScreens.UPGRADE);

      // Check if we already have the price in the upgrade preview data
      if (
        !!upgradePreviewData?.items.find((item) => item.price.id === priceId)
      ) {
        return;
      } else if (!!upgradePreviewData) {
        setUpgradePreviewData(undefined);
      }

      getUpgradePreview.mutate({
        subscriptionId: subscription.subscriptionId,
        priceId: priceId,
      });

      /* Analytics */
      const product = PRICE_ID_TO_PLAN_MAP[priceId];
      const interval = PRICE_ID_TO_INTERVAL_MAP[priceId];

      trackEvent("Upgrade Started", {
        plan: product,
        interval: interval,
        origin: origin,
      });
    },
    [
      getStripeChangePreview,
      getUpgradePreview,
      isPaddleLoading,
      origin,
      paddleSubscription,
      returnToPricing,
      sendToastMessage,
      setActivePaddleScreen,
      setStripeChangePreview,
      setUpgradePreviewData,
      subscription,
      upgradePreviewData,
      trackEvent,
    ]
  );

  const updateNumberOfAdditionalVoices = useCallback(
    async (numberOfVoices: number) => {
      if (!subscription) {
        return;
      }

      const subscriptionPreviewItem = upgradePreviewData?.items.find((item) =>
        Object.values(ProductId).includes(item.price.product_id)
      );

      if (!subscriptionPreviewItem) {
        return;
      }

      setIsUpgradeUpdating(true);

      try {
        await getUpgradePreview.mutateAsync({
          subscriptionId: subscription.subscriptionId,
          priceId: subscriptionPreviewItem.price.id,
          numberOfAdditionalVoiceSlots: numberOfVoices,
        });
      } catch {
        // onError already toasts and returns to pricing
      } finally {
        setIsUpgradeUpdating(false);
      }
    },
    [getUpgradePreview, setIsUpgradeUpdating, subscription, upgradePreviewData]
  );

  const closeUpgrade = useCallback(() => {
    if (isProcessing) {
      return;
    }
    setOrigin(undefined);
    setStripeChangePreview(undefined);
    setActivePaddleScreen(null);
  }, [isProcessing, setActivePaddleScreen, setOrigin, setStripeChangePreview]);

  const confirmStripeChange = useCallback(async () => {
    if (!subscription || !stripeChangePreview) {
      return;
    }

    await changeStripeSubscription.mutateAsync({
      subscriptionId: subscription.subscriptionId,
      priceId: stripeChangePreview.targetPriceId,
    });
  }, [changeStripeSubscription, stripeChangePreview, subscription]);

  const upgradeSubsription = useCallback(
    async (priceId: TPriceId, numberOfAdditionalVoices?: number) => {
      if (!subscription) {
        return;
      }

      await changeSubscription.mutateAsync({
        subscriptionId: subscription.subscriptionId,
        priceId: priceId,
        numberOfAdditionalVoices: numberOfAdditionalVoices,
      });
    },
    [changeSubscription, subscription]
  );

  return {
    startUpgrade,
    closeUpgrade,
    upgradeSubsription,
    updateNumberOfAdditionalVoices,
    isUpgradeUpdating,
    confirmStripeChange,
  };
};
